import React, { useMemo } from 'react'
import {
  EyeOutlined,
  FileTextOutlined,
  GoldOutlined,
  RiseOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import useAuth from '@lib/auth'
import useWebPlatformLevel, { IWebPlatformLevel } from '@/hooks/useWebPlatform'

type TabAuthUrl = string | Partial<Record<IWebPlatformLevel, string>>
type PermissionSource = 'authByUrl' | 'functionAuthByUrl'

export interface TabsConfig {
  key: string
  tabLabelI18nKey: string
  authUrl: TabAuthUrl
  permissionSource: PermissionSource
  forceVisible?: boolean
  /** authUrl 僅用於 tab 權限判斷時，設為 true 以從 SideMenu 隱藏 */
  hideFromSideMenu?: boolean
  icon: React.ComponentType<any>
  usageBlockI18nKey?: string
  Component: React.LazyExoticComponent<React.ComponentType<any>>
}

const tabsConfig: TabsConfig[] = [
  {
    key: 'webSettingSection',
    tabLabelI18nKey: 'menu:admin-web-setting',
    icon: ToolOutlined,
    usageBlockI18nKey: 'tab1Description',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/WebSettingSection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-web-setting',
      Regular: '/companies/:companyCode/web-setting',
    },
  },
  {
    key: 'unitConversionSection',
    hideFromSideMenu: true,
    tabLabelI18nKey: 'menu:admin-unit-conversion-management',
    icon: SwapOutlined,
    usageBlockI18nKey: 'tab2Description',
    Component: React.lazy(
      () => import('src/containers/WebSettingV2Sections/UnitConversionSection')
    ),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-unit-conversion-management',
      Regular: '/companies/:companyCode/unit-conversion-management',
    },
  },
  {
    key: 'uncertaintySection',
    hideFromSideMenu: true,
    tabLabelI18nKey: 'menu:admin-uncertainty-setting',
    icon: RiseOutlined,
    usageBlockI18nKey: 'tab3Description',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/UncertaintySection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-uncertainty-setting',
      Regular: '/companies/:companyCode/uncertainty-setting',
    },
  },
  {
    key: 'FieldSettingSection',
    hideFromSideMenu: true,
    tabLabelI18nKey: 'tab4Title',
    icon: EyeOutlined,
    usageBlockI18nKey: 'tab4Description',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/FieldSettingSection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-field-setting',
      Regular: '/companies/:companyCode/field-setting',
    },
  },
  {
    key: 'simpleModeSection',
    hideFromSideMenu: true,
    tabLabelI18nKey: 'tab5Title',
    icon: ThunderboltOutlined,
    usageBlockI18nKey: 'tab5Description',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/SimpleModeSection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-uncertainty-setting',
      Regular: '/companies/:companyCode/uncertainty-setting',
    },
  },
  {
    key: 'productSection',
    hideFromSideMenu: true,
    tabLabelI18nKey: 'tab6Title',
    icon: GoldOutlined,
    usageBlockI18nKey: 'tab6Description',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/ProductSection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-product-setting',
      Regular: '/companies/:companyCode/product-setting',
    },
  },
  {
    key: 'systemLogSection',
    tabLabelI18nKey: 'logDownloadTitle',
    icon: FileTextOutlined,
    usageBlockI18nKey: 'logDownloadDescription',
    Component: React.lazy(() => import('src/containers/WebSettingV2Sections/SystemLogSection')),
    permissionSource: 'authByUrl',
    authUrl: {
      Admin: '/admin-web-setting',
    },
  },
]

const collectAuthUrls = (authUrl: TabAuthUrl) => {
  if (typeof authUrl === 'string') {
    return [authUrl]
  }

  return Object.values(authUrl).filter(Boolean) as string[]
}

const resolveAuthUrl = (authUrl: TabAuthUrl, platformLevel: IWebPlatformLevel) => {
  if (typeof authUrl === 'string') {
    return authUrl
  }

  return authUrl[platformLevel] || authUrl.Admin || authUrl.Regular || authUrl.Global || ''
}

export const getSideMenuHiddenAuthUrls = () =>
  new Set(
    tabsConfig.filter(tab => tab.hideFromSideMenu).flatMap(tab => collectAuthUrls(tab.authUrl))
  )

export const useTabsConfig = () => {
  const { user } = useAuth()
  const platformLevel = useWebPlatformLevel()

  return useMemo(() => {
    const authByUrl = user?.authByUrl || {}
    const functionAuthByUrl = user?.functionAuthByUrl || {}
    const readableAuthUrls = new Set(
      Object.entries(authByUrl)
        .filter(([, auth]) => auth.readable)
        .map(([authUrl]) => authUrl)
    )

    return tabsConfig.filter(tab => {
      if (tab.forceVisible) {
        return true
      }

      // 依目前平台層級取對應的權限路徑
      const authUrl = resolveAuthUrl(tab.authUrl, platformLevel)

      if (!authUrl) {
        return false
      }

      if (tab.permissionSource === 'functionAuthByUrl') {
        // 任一功能權限開啟，即顯示該 tab
        const functionAuth = functionAuthByUrl[authUrl]
        return !!(functionAuth?.import || functionAuth?.authEnable || functionAuth?.authDisable)
      }

      // authByUrl 只判斷 readable
      return readableAuthUrls.has(authUrl)
    })
  }, [platformLevel, user?.authByUrl, user?.functionAuthByUrl])
}
